import { PageFormatType, Resume } from '@rio/types'
import { useI18n } from 'builder/hooks/useI18n'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import { useUpdateTemplateSettings } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/hooks/useUpdateTemplateSettings'
import { useTemplateCustomization } from 'builder/components/DocumentCustomizationModal'
import { DEFAULT_TEMPLATE_SETTINGS } from 'builder/modules/resumeEditor/constants'
import { useDateFormatCustomisation } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/hooks/useDateFormatCustomisation'
import { HeaderAlignmentSelection } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/components/LayoutCustomization/HeaderAlignmentSelection'
import { DateAlignmentSelection } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/components/LayoutCustomization/DateAlignmentSelection'
import { LocationAlignmentSelection } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/components/LayoutCustomization/LocationAlignmentSelection'
import { SkillBlockLayoutSelection } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/components/LayoutCustomization/SkillBlockLayoutSelection'
import { usePageFormatCustomisation } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/hooks/usePageFormatCustomisation'
import { EducationBlockOrderSelection } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/components/LayoutCustomization/EducationBlockOrderSelection'
import { EducationBlockLayoutSelection } from 'builder/components/DocumentCustomizationModal/components/ResumeSidebar/components/LayoutCustomization/EducationBlockLayoutSelection'
import { SelectInput } from '../SelectInput/SelectInput'
import { SlideInput } from '../SlideInput/SlideInput'
import { CustomSelect } from '../CustomSelect/CustomSelect'
import * as Styled from './styles'

type Props = {
  resume: Resume
}

const TRANSLATION = 'builder.resume_editor.document_customization.sidebar.layout_tab_new_design'

export const LayoutContent = ({ resume }: Props) => {
  const { i18n } = useI18n()
  const { isPhone } = useMediaQueries()
  const templateCustomisation = useTemplateCustomization(resume)
  const { updateTemplateSettings } = useUpdateTemplateSettings(resume)

  const pageFormat = usePageFormatCustomisation(resume, value =>
    updateTemplateSettings({ settingsName: 'format', value: value as PageFormatType }),
  )
  const dateFormat = useDateFormatCustomisation(resume, (settingsName, value) =>
    updateTemplateSettings({ settingsName, value }),
  )

  const { currentTemplate } = templateCustomisation
  const currentSettings = templateCustomisation.resumeSpecificTemplateSettings
  const defaultSettings = templateCustomisation.currentTemplate?.settings

  // Spacing settings field values
  const verticalPageMargins =
    currentSettings?.verticalPageMargins ??
    defaultSettings?.verticalPageMargins?.default ??
    DEFAULT_TEMPLATE_SETTINGS.verticalPageMargins

  const horizontalPageMargins =
    currentSettings?.horizontalPageMargins ??
    defaultSettings?.horizontalPageMargins?.default ??
    DEFAULT_TEMPLATE_SETTINGS.horizontalPageMargins

  const marginsBetweenSections =
    currentSettings?.marginsBetweenSections ??
    defaultSettings?.marginsBetweenSections?.default ??
    DEFAULT_TEMPLATE_SETTINGS.marginsBetweenSections

  const marginsBetweenSectionNameAndContent =
    currentSettings?.marginsBetweenSectionNameAndContent ??
    defaultSettings?.marginsBetweenSectionNameAndContent?.default ??
    DEFAULT_TEMPLATE_SETTINGS.marginsBetweenSectionNameAndContent

  const marginsBetweenCards =
    currentSettings?.marginsBetweenCards ??
    defaultSettings?.marginsBetweenCards?.default ??
    DEFAULT_TEMPLATE_SETTINGS.marginsBetweenCards

  return (
    <Styled.Container>
      <CustomSelect
        label={i18n.t(`${TRANSLATION}.format`)}
        selected={pageFormat.selectedLayoutFormatOption}
        disabled={!pageFormat.supportsLayoutFormats}
        options={pageFormat.letterFormatOptions}
        useNativeSelectOnMobile={false}
        buttonOnClick={pageFormat.onButtonClick}
        onSelect={pageFormat.onSelect}
      />

      <Styled.GroupControl
        color="Neutral50"
        size="Caps"
        spacing={isPhone ? 'M' : 'L'}
        title={i18n.t(`${TRANSLATION}.margin_and_paddings`)}
      >
        <SlideInput
          unit=" in"
          name={i18n.t(`${TRANSLATION}.vertical_page_margins`)}
          disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
          disabled={!currentTemplate?.supportsVerticalPageMargins}
          value={verticalPageMargins}
          defaultValue={defaultSettings?.verticalPageMargins?.default}
          min={defaultSettings?.verticalPageMargins?.min}
          max={defaultSettings?.verticalPageMargins?.max}
          step={defaultSettings?.verticalPageMargins?.step}
          onChange={value => updateTemplateSettings({ settingsName: 'verticalPageMargins', value })}
        />
        <SlideInput
          unit=" in"
          name={i18n.t(`${TRANSLATION}.horizontal_page_margins`)}
          disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
          disabled={!currentTemplate?.supportsHorizontalPageMargins}
          value={horizontalPageMargins}
          defaultValue={defaultSettings?.horizontalPageMargins?.default}
          min={defaultSettings?.horizontalPageMargins?.min}
          max={defaultSettings?.horizontalPageMargins?.max}
          step={defaultSettings?.horizontalPageMargins?.step}
          onChange={value =>
            updateTemplateSettings({ settingsName: 'horizontalPageMargins', value })
          }
        />
        <SlideInput
          unit=" pt"
          name={i18n.t(`${TRANSLATION}.margins_between_sections`)}
          disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
          disabled={!currentTemplate?.supportsMarginsBetweenSections}
          value={marginsBetweenSections}
          defaultValue={defaultSettings?.marginsBetweenSections?.default}
          min={defaultSettings?.marginsBetweenSections?.min}
          max={defaultSettings?.marginsBetweenSections?.max}
          step={defaultSettings?.marginsBetweenSections?.step}
          onChange={value =>
            updateTemplateSettings({ settingsName: 'marginsBetweenSections', value })
          }
        />
        <SlideInput
          unit=" pt"
          name={i18n.t(`${TRANSLATION}.margins_between_section_name_and_content`)}
          disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
          disabled={!currentTemplate?.supportsMarginsBetweenSectionNameAndContent}
          value={marginsBetweenSectionNameAndContent}
          defaultValue={defaultSettings?.marginsBetweenSectionNameAndContent?.default}
          min={defaultSettings?.marginsBetweenSectionNameAndContent?.min}
          max={defaultSettings?.marginsBetweenSectionNameAndContent?.max}
          step={defaultSettings?.marginsBetweenSectionNameAndContent?.step}
          onChange={value =>
            updateTemplateSettings({ settingsName: 'marginsBetweenSectionNameAndContent', value })
          }
        />
        <SlideInput
          unit=" pt"
          name={i18n.t(`${TRANSLATION}.margins_between_cards`)}
          disabledTooltip={i18n.t(`${TRANSLATION}.margin_customization_warning`)}
          disabled={!currentTemplate?.supportsMarginsBetweenCards}
          value={marginsBetweenCards}
          defaultValue={defaultSettings?.marginsBetweenCards?.default}
          min={defaultSettings?.marginsBetweenCards?.min}
          max={defaultSettings?.marginsBetweenCards?.max}
          step={defaultSettings?.marginsBetweenCards?.step}
          onChange={value => updateTemplateSettings({ settingsName: 'marginsBetweenCards', value })}
        />
      </Styled.GroupControl>

      <SelectInput
        name={i18n.t(`${TRANSLATION}.date_format`)}
        options={dateFormat.options}
        selected={dateFormat.value}
        onSelect={dateFormat.onSelect}
        disabled={!currentTemplate?.supportsDateFormatCustomization}
        disabledTooltip={i18n.t(`${TRANSLATION}.date_format_warning`)}
      />

      <Styled.GroupControl
        title={i18n.t(`${TRANSLATION}.header_alignment`)}
        color="Neutral50"
        size="Caps"
      >
        <HeaderAlignmentSelection
          resume={resume}
          styles={Styled}
          onFieldChange={(settingsName, value) => updateTemplateSettings({ settingsName, value })}
        />
      </Styled.GroupControl>

      <Styled.GroupControl
        title={i18n.t(`${TRANSLATION}.date_alignment`)}
        color="Neutral50"
        size="Caps"
      >
        <DateAlignmentSelection
          resume={resume}
          styles={Styled}
          translation={TRANSLATION}
          onFieldChange={(settingsName, value) => updateTemplateSettings({ settingsName, value })}
        />
      </Styled.GroupControl>

      <Styled.GroupControl
        title={i18n.t(`${TRANSLATION}.location_alignment`)}
        color="Neutral50"
        size="Caps"
      >
        <LocationAlignmentSelection
          resume={resume}
          styles={Styled}
          onFieldChange={(settingsName, value) => updateTemplateSettings({ settingsName, value })}
        />
      </Styled.GroupControl>

      <Styled.GroupControl
        title={i18n.t(`${TRANSLATION}.skills_layout`)}
        color="Neutral50"
        size="Caps"
      >
        <SkillBlockLayoutSelection
          resume={resume}
          styles={Styled}
          translation={TRANSLATION}
          onFieldChange={(settingsName, value) => updateTemplateSettings({ settingsName, value })}
        />
      </Styled.GroupControl>

      <Styled.GroupControl
        title={i18n.t(`${TRANSLATION}.education_block_order`)}
        color="Neutral50"
        size="Caps"
      >
        <EducationBlockOrderSelection
          resume={resume}
          styles={Styled}
          onFieldChange={(settingsName, value) => updateTemplateSettings({ settingsName, value })}
        />
      </Styled.GroupControl>

      <Styled.GroupControl
        title={i18n.t(`${TRANSLATION}.education_block_layout`)}
        color="Neutral50"
        size="Caps"
      >
        <EducationBlockLayoutSelection
          resume={resume}
          styles={Styled}
          onFieldChange={(settingsName, value) => updateTemplateSettings({ settingsName, value })}
        />
      </Styled.GroupControl>
    </Styled.Container>
  )
}
